/**
 * Routing Rule Sheet — create/edit an inbound routing rule in place.
 *
 * The form used to live inside RoutingTab, reachable only by navigating into a
 * project card. The analysis-setup hub lists rules for every project, so
 * "Add Rule" there had to bounce the user out to `/projects/$projectId/settings`
 * — a jarring detour that also made it easy to lose track of which project the
 * rule belonged to. Extracting the sheet lets both surfaces open the same form,
 * with `projectId` passed explicitly so a rule is always created in the project
 * whose card it was opened from.
 *
 * Owns its own mutations: callers only say which project and which rule (null =
 * create), and get a callback when the work is done.
 */

import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DELIVERY_RULE_PRIORITY_MAX } from '@aica/core';
import { Save, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetBody,
} from '@/components/ui/sheet';
import { CRMFieldsManager } from '@/components/settings/CRMFieldsManager';
import { RoutingRuleBuilder } from './RoutingRuleBuilder';
import {
  useCrmFields,
  useDepartments,
  useCreateInboundRoutingRule,
  useUpdateInboundRoutingRule,
  transformFieldsForQueryBuilder,
} from '@/features/routing/hooks';
import {
  getInboundRoutingFields,
  mergeCustomCrmFields,
  enrichFieldsWithDepartments,
} from '@/features/routing/constants/routing-fields';
import { validateFieldsExist } from '@/features/routing/utils/jsonlogic-adapter';
import { isPriorityTaken } from '@/features/routing/utils/routing-errors';
import type {
  InboundRoutingRule,
  CreateInboundRoutingRuleRequest,
  UpdateInboundRoutingRuleRequest,
  CrmFieldsEntityResponse,
} from '@/lib/api/types';

export interface RoutingRuleSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Project the rule belongs to. Required even when editing — a rule is never org-wide. */
  projectId: string;
  /** Rule being edited, or null to create a new one. */
  rule?: InboundRoutingRule | null;
  /** Fired after a successful create/update, once the sheet has closed. */
  onSaved?: () => void;
}

export function RoutingRuleSheet({
  open,
  onOpenChange,
  projectId,
  rule = null,
  onSaved,
}: RoutingRuleSheetProps) {
  const { t } = useTranslation(['settings', 'common']);
  const createRule = useCreateInboundRoutingRule({ priorityTakenAtField: true });
  const updateRule = useUpdateInboundRoutingRule({ priorityTakenAtField: true });

  const handleSubmit = async (
    data: CreateInboundRoutingRuleRequest | UpdateInboundRoutingRuleRequest
  ) => {
    if (rule) {
      await updateRule.mutateAsync({ id: rule.id, data });
    } else {
      await createRule.mutateAsync(data as CreateInboundRoutingRuleRequest);
    }
    onOpenChange(false);
    onSaved?.();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {rule
              ? t('routing.editRule', 'Edit Routing Rule')
              : t('routing.createRule', 'Create Routing Rule')}
          </SheetTitle>
          <SheetDescription>
            {t(
              'routing.formDescription',
              'Define conditions that determine when records should be routed to this project.'
            )}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          {/* Remount on project/rule change so the form's initial state is not
              stale — the hub can open this sheet for a different project card
              without the component ever unmounting. */}
          <InboundRoutingForm
            key={`${projectId}:${rule?.id ?? 'new'}`}
            projectId={projectId}
            rule={rule}
            onSubmit={handleSubmit}
            onCancel={() => onOpenChange(false)}
            isSubmitting={createRule.isPending || updateRule.isPending}
          />
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

// ============================================================================
// Inbound Routing Form Component
// ============================================================================

interface InboundRoutingFormProps {
  projectId: string;
  rule: InboundRoutingRule | null;
  onSubmit: (
    data: CreateInboundRoutingRuleRequest | UpdateInboundRoutingRuleRequest
  ) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

function InboundRoutingForm({
  projectId,
  rule,
  onSubmit,
  onCancel,
  isSubmitting,
}: InboundRoutingFormProps) {
  const { t } = useTranslation(['settings', 'common']);

  const [name, setName] = useState(rule?.name || '');
  const [description, setDescription] = useState(rule?.description || '');
  // Empty on a new rule: the server places it last in the organization's
  // order (#2893) — a fixed 100 landed it mid-order after any reorder.
  const [priority, setPriority] = useState<number | ''>(rule?.priority ?? '');
  const [isEnabled, setIsEnabled] = useState(rule?.isEnabled ?? true);
  const [conditions, setConditions] = useState<Record<string, unknown>>(
    rule?.conditions || {}
  );
  const [missingFieldsWarning, setMissingFieldsWarning] = useState<string[] | null>(null);
  const [priorityTaken, setPriorityTaken] = useState(false);

  // Fetch all CRM fields (no entityType filter for inbound routing)
  const { data: crmFieldsResponse } = useCrmFields({ enabled: true });

  // Fetch departments for dropdown
  const { data: departments } = useDepartments();

  // Merge base routing fields with dynamic CRM fields from all entity types
  const mergedFields = useMemo(() => {
    let fields = getInboundRoutingFields(t);
    const responseData = crmFieldsResponse?.data;

    if (responseData) {
      // When entityType is provided, response is CrmFieldsEntityResponse
      if (
        'fields' in responseData &&
        'entityType' in responseData &&
        Array.isArray(responseData.fields)
      ) {
        const entityResponse = responseData as CrmFieldsEntityResponse;
        const transformedFields = transformFieldsForQueryBuilder(entityResponse);
        fields = mergeCustomCrmFields(transformedFields, getInboundRoutingFields(t), t);
      }
      // When no entityType filter (inbound routing), response is Record<entityType, { fields, updatedAt }>
      else if (typeof responseData === 'object' && !Array.isArray(responseData)) {
        const allTransformedFields: Array<{
          fieldKey: string;
          fieldName: string;
          fieldType: string;
          entityType: string;
          possibleValues?: Array<{ value: string; label: string }>;
        }> = [];

        // Iterate over all entity types (deal, lead, contact, company)
        for (const [entityType, entityData] of Object.entries(responseData)) {
          if (entityData && 'fields' in entityData && Array.isArray(entityData.fields)) {
            const transformed = transformFieldsForQueryBuilder({
              entityType,
              fields: entityData.fields,
              updatedAt: entityData.updatedAt || null,
              cacheStatus: 'cached',
            } as CrmFieldsEntityResponse);
            allTransformedFields.push(...transformed);
          }
        }

        fields = mergeCustomCrmFields(
          allTransformedFields,
          getInboundRoutingFields(t),
          t
        );
      }
    }

    // Enrich department field with dropdown values
    return enrichFieldsWithDepartments(fields, departments);
  }, [crmFieldsResponse, departments, t]);

  const handleConditionsChange = useCallback(
    (jsonLogic: Record<string, unknown> | null) => {
      setConditions(jsonLogic || {});
      // Clear warning when conditions change
      setMissingFieldsWarning(null);
    },
    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that CRM fields used in conditions exist in cache
    if (Object.keys(conditions).length > 0) {
      const missingFields = validateFieldsExist(conditions, mergedFields);
      if (missingFields.length > 0) {
        setMissingFieldsWarning(missingFields);
        // Don't block save, just show warning
      }
    }

    const conditionsJson =
      Object.keys(conditions).length > 0 ? JSON.stringify(conditions) : undefined;
    // Empty only on a new rule: an existing rule's field is required.
    const submittedPriority = priority === '' ? undefined : priority;
    // An edit sends the priority only when the user changed it: the field was
    // seeded from the list, and a reorder elsewhere since then would turn a
    // rename into a move back to the old place — or a PRIORITY_TAKEN refusal
    // for a field nobody touched (#2949).
    const priorityChanged = rule !== null && submittedPriority !== rule.priority;

    const payload = rule
      ? {
          name,
          description: description || null,
          conditionsJson,
          ...(priorityChanged && { priority: submittedPriority }),
          isEnabled,
        }
      : {
          projectId,
          name,
          description: description || undefined,
          conditionsJson,
          priority: submittedPriority,
          isEnabled,
        };
    try {
      await onSubmit(payload);
    } catch (error) {
      // Shown at the field, where it is fixed (the hooks are told to skip their
      // toast for it); any other refusal keeps its toast.
      if (isPriorityTaken(error)) {
        setPriorityTaken(true);
        return;
      }
      throw error;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name */}
      <div>
        <label
          htmlFor="rule-name"
          className="block text-sm font-medium text-fg-secondary mb-1.5"
        >
          {t('routing.nameLabel', 'Rule Name')} *
        </label>
        <Input
          id="rule-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder={t('routing.namePlaceholder', 'e.g., Route to Sales Team')}
        />
      </div>

      {/* Description */}
      <div>
        <label
          htmlFor="rule-description"
          className="block text-sm font-medium text-fg-secondary mb-1.5"
        >
          {t('routing.descriptionLabel', 'Description')}
        </label>
        <Textarea
          id="rule-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          minRows={2}
          placeholder={t(
            'routing.descriptionPlaceholder',
            'Optional description for this rule'
          )}
        />
      </div>

      {/* Priority & Enabled */}
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label
            htmlFor="rule-priority"
            className="block text-sm font-medium text-fg-secondary mb-1.5"
          >
            {t('routing.priorityLabel', 'Priority')}
          </label>
          <Input
            id="rule-priority"
            type="number"
            min={1}
            max={DELIVERY_RULE_PRIORITY_MAX}
            required={rule !== null}
            value={priority}
            onChange={(e) => {
              setPriority(e.target.value === '' ? '' : Number(e.target.value));
              setPriorityTaken(false);
            }}
            aria-invalid={priorityTaken || undefined}
            aria-describedby={priorityTaken ? 'rule-priority-taken' : undefined}
            placeholder={
              rule ? undefined : t('routing.priorityPlaceholder', 'After the last rule')
            }
          />
          {priorityTaken ? (
            <p id="rule-priority-taken" role="alert" className="text-xs text-danger mt-1">
              {t(
                'routing.priorityTaken',
                "This priority is taken by another rule — possibly in a project you don't see or an archived one. Choose a different one."
              )}
            </p>
          ) : (
            <p className="text-xs text-fg-muted mt-1">
              {t('routing.priorityHint', 'Lower numbers are evaluated first')}
            </p>
          )}
        </div>

        <div className="flex items-start gap-3 pt-6">
          <Switch
            id="rule-enabled"
            checked={isEnabled}
            onCheckedChange={setIsEnabled}
            size="sm"
          />
          <div>
            <label htmlFor="rule-enabled" className="text-sm font-medium text-fg">
              {t('routing.enabledLabel', 'Enabled')}
            </label>
            <p className="text-xs text-fg-muted mt-0.5">
              {t('routing.enabledHint', 'Disabled rules are skipped during evaluation')}
            </p>
          </div>
        </div>
      </div>

      {/* CRM Fields Status & Refresh */}
      <CRMFieldsManager />

      {/* Conditions Builder */}
      <div>
        <label className="block text-sm font-medium text-fg-secondary mb-1.5">
          {t('routing.conditionsLabel', 'Routing Conditions')}
        </label>
        <p className="text-xs text-fg-muted mb-3">
          {t(
            'routing.conditionsHint',
            'Define conditions using CRM fields. Records matching these conditions will be routed to this project.'
          )}
        </p>
        <RoutingRuleBuilder
          initialValue={rule?.conditions}
          onChange={handleConditionsChange}
          showPreview={true}
          compact
          fields={mergedFields}
        />
      </div>

      {/* Missing Fields Warning */}
      {missingFieldsWarning && missingFieldsWarning.length > 0 && (
        <div className="rounded-lg border border-border bg-bg-muted p-3 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-fg-muted shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-fg">
              {t('routing.missingFieldsWarning', 'Some CRM fields may not exist')}
            </p>
            <p className="text-xs text-fg-muted mt-1">
              {t(
                'routing.missingFieldsDescription',
                'The following fields are used in conditions but not found in CRM cache:'
              )}
            </p>
            <ul className="text-xs text-fg-muted mt-1 list-disc list-inside">
              {missingFieldsWarning.map((field) => (
                <li key={field} className="font-mono">
                  {field}
                </li>
              ))}
            </ul>
            <p className="text-xs text-fg-muted mt-2">
              {t(
                'routing.missingFieldsHint',
                'You can still save this rule. Try refreshing CRM fields cache if these fields exist.'
              )}
            </p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 pt-4 border-t border-border flex items-center gap-3">
        <Button
          type="submit"
          isLoading={isSubmitting}
          disabled={isSubmitting || !name.trim()}
          size="sm"
        >
          {!isSubmitting && <Save className="h-4 w-4 mr-2" />}
          {rule
            ? t('common:common.saveChanges', 'Save Changes')
            : t('routing.createRule', 'Create Rule')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t('common:common.cancel', 'Cancel')}
        </Button>
      </div>
    </form>
  );
}
